import {
  CreateZakatAssetRequest,
  CreateZakatLiabilityRequest,
  UpdateDraftZakatRequest,
  UpdateZakatAssetRequest,
  UpdateZakatLiabilityRequest,
  UpdateZakatRequest,
  Zakat,
  ZakatAsset,
  ZakatLiability,
} from "@ifgengineering/client-invest-sdk";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { clientInvestSdk } from "@helpers/clientInvestSdk";
import {
  FETCH_NISAB,
  FETCH_ZAKAT,
  UPDATE_ZAKAT,
  CREATE_ZAKAT_ASSET,
  UPDATE_ZAKAT_ASSET,
  DELETE_ZAKAT_ASSET,
  CREATE_ZAKAT_LIABILITY,
  UPDATE_ZAKAT_LIABILITY,
  DELETE_ZAKAT_LIABILITY,
  FETCH_DRAFT_ZAKAT,
  CREATE_DRAFT_ZAKAT,
  UPDATE_DRAFT_ZAKAT,
  CLONE_LATEST_ZAKAT,
  FETCH_ALL_ZAKATS,
  UPDATE_ZAKAT_TOP_UP,
  UPDATE_ZAKAT_PAYMENT_AMOUNT,
} from "./zakat.types";

interface FetchZakatArgs {
  isZakat2026Enabled: boolean;
}

export const fetchNisabAsync = createAsyncThunk<number>(
  FETCH_NISAB,
  async () => {
    const response = await clientInvestSdk.getNisab();
    return response.data;
  }
);

export const fetchZakatAsync = createAsyncThunk<Zakat, FetchZakatArgs>(
  FETCH_ZAKAT,
  async ({ isZakat2026Enabled }) => {
    // When FF_ZAKAT_2026 is enabled, use /zakat-v2 (draft endpoint) as the primary zakat
    if (isZakat2026Enabled) {
      const response = await clientInvestSdk.getDraftZakat();
      // If no draft exists, fall back to legacy endpoint
      if (response.data) {
        return response.data;
      }
    }
    const response = await clientInvestSdk.getZakat();
    return response.data;
  }
);

export const updateZakatAsync = createAsyncThunk<Zakat, UpdateZakatRequest>(
  UPDATE_ZAKAT,
  async (payload) => {
    const response = await clientInvestSdk.updateZakat(payload);
    return response.data;
  }
);

export const createZakatAssetAsync = createAsyncThunk<
  ZakatAsset,
  CreateZakatAssetRequest
>(CREATE_ZAKAT_ASSET, async (payload) => {
  const response = await clientInvestSdk.createZakatAsset(payload);
  return response.data;
});

export const updateZakatAssetAsync = createAsyncThunk<
  ZakatAsset,
  { assetId: string; data: UpdateZakatAssetRequest }
>(UPDATE_ZAKAT_ASSET, async ({ assetId, data }) => {
  const response = await clientInvestSdk.updateZakatAsset(assetId, data);
  return response.data;
});

export const deleteZakatAssetAsync = createAsyncThunk<string, string>(
  DELETE_ZAKAT_ASSET,
  async (assetId) => {
    await clientInvestSdk.deleteZakatAsset(assetId);
    return assetId;
  }
);

export const createZakatLiabilityAsync = createAsyncThunk<
  ZakatLiability,
  CreateZakatLiabilityRequest
>(CREATE_ZAKAT_LIABILITY, async (payload) => {
  const response = await clientInvestSdk.createZakatLiability(payload);
  return response.data;
});

export const updateZakatLiabilityAsync = createAsyncThunk<
  ZakatLiability,
  { liabilityId: string; data: UpdateZakatLiabilityRequest }
>(UPDATE_ZAKAT_LIABILITY, async ({ liabilityId, data }) => {
  const response = await clientInvestSdk.updateZakatLiability(
    liabilityId,
    data
  );
  return response.data;
});

export const deleteZakatLiabilityAsync = createAsyncThunk<string, string>(
  DELETE_ZAKAT_LIABILITY,
  async (liabilityId) => {
    await clientInvestSdk.deleteZakatLiability(liabilityId);
    return liabilityId;
  }
);

export const updateZakatPaymentAmount = createAction<number, string>(
  UPDATE_ZAKAT_PAYMENT_AMOUNT
);

export const updateZakatTopUp = createAction<number, string>(
  UPDATE_ZAKAT_TOP_UP
);

// Draft Zakat Actions (FF_ZAKAT_2026)
export const fetchDraftZakatAsync = createAsyncThunk<Zakat | null>(
  FETCH_DRAFT_ZAKAT,
  async () => {
    const response = await clientInvestSdk.getDraftZakat();
    return response.data;
  }
);

export const createDraftZakatAsync = createAsyncThunk<Zakat>(
  CREATE_DRAFT_ZAKAT,
  async () => {
    const response = await clientInvestSdk.createDraftZakat();
    return response.data;
  }
);

export const updateDraftZakatAsync = createAsyncThunk<
  Zakat,
  UpdateDraftZakatRequest
>(UPDATE_DRAFT_ZAKAT, async (payload) => {
  const response = await clientInvestSdk.updateDraftZakat(payload);
  return response.data;
});

export const cloneLatestZakatAsync = createAsyncThunk<Zakat>(
  CLONE_LATEST_ZAKAT,
  async () => {
    const response = await clientInvestSdk.cloneLatestZakat();
    return response.data;
  }
);

// Zakat History
export const fetchAllZakatsAsync = createAsyncThunk<Zakat[]>(
  FETCH_ALL_ZAKATS,
  async () => {
    const response = await clientInvestSdk.getAllZakats();
    return response.data;
  }
);
